export type LogValue =
  | boolean
  | number
  | string
  | null
  | undefined
  | LogValue[]
  | { [key: string]: LogValue };

export interface LogContext {
  [key: string]: unknown;
}

export interface LoggerLike {
  debug(context: Record<string, unknown>, message: string): void;
  info(context: Record<string, unknown>, message: string): void;
  warn(context: Record<string, unknown>, message: string): void;
  error(context: Record<string, unknown>, message: string): void;
  fatal?(context: Record<string, unknown>, message: string): void;
  child(bindings: Record<string, unknown>): LoggerLike;
}

export interface StructuredLogger {
  child(bindings: LogContext): StructuredLogger;
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, context?: ErrorLogContext): void;
  fatal(message: string, context?: ErrorLogContext): void;
}

export interface ObservedUser {
  id: string;
  email?: string | null;
  wallet?: string | null;
}

export interface ExceptionContext {
  extra?: Record<string, unknown>;
  requestId?: string;
  tags?: Record<string, string>;
  user?: ObservedUser;
}

export interface ErrorLogContext extends LogContext {
  err?: unknown;
  error?: unknown;
  sentry?: ExceptionContext;
}

interface ErrorLogObserverInput {
  bindings: LogContext;
  context: ErrorLogContext;
  error: unknown;
  level: 'error' | 'fatal';
  message: string;
}

interface StructuredLoggerOptions {
  bindings?: LogContext;
  onErrorLog?: (input: ErrorLogObserverInput) => void;
}

export function buildRequestId(incomingRequestId?: string | null) {
  const normalizedRequestId = incomingRequestId?.trim();
  if (normalizedRequestId) {
    return normalizedRequestId;
  }

  return crypto.randomUUID();
}

export function createStructuredLogger(
  logger: LoggerLike,
  options: StructuredLoggerOptions = {},
): StructuredLogger {
  const bindings = options.bindings ?? {};

  return {
    child(bindings) {
      return createStructuredLogger(logger.child(bindings), {
        ...options,
        bindings: {
          ...(options.bindings ?? {}),
          ...bindings,
        },
      });
    },
    debug(message, context) {
      logger.debug(serializeLogContext(context), message);
    },
    info(message, context) {
      logger.info(serializeLogContext(context), message);
    },
    warn(message, context) {
      logger.warn(serializeLogContext(context), message);
    },
    error(message, context) {
      logger.error(serializeLogContext(context), message);
      observeErrorLog({
        bindings,
        context,
        level: 'error',
        message,
        onErrorLog: options.onErrorLog,
      });
    },
    fatal(message, context) {
      if (logger.fatal) {
        logger.fatal(serializeLogContext(context), message);
      } else {
        logger.error(serializeLogContext(context), message);
      }

      observeErrorLog({
        bindings,
        context,
        level: 'fatal',
        message,
        onErrorLog: options.onErrorLog,
      });
    },
  };
}

function observeErrorLog(input: {
  bindings: LogContext;
  context?: ErrorLogContext;
  level: 'error' | 'fatal';
  message: string;
  onErrorLog?: (input: ErrorLogObserverInput) => void;
}) {
  if (input.onErrorLog === undefined || input.context === undefined) {
    return;
  }

  const error = readLoggedError(input.context);

  if (error === undefined) {
    return;
  }

  input.onErrorLog({
    bindings: input.bindings,
    context: {
      ...input.bindings,
      ...input.context,
    },
    error,
    level: input.level,
    message: input.message,
  });
}

function readLoggedError(context: ErrorLogContext) {
  if (context.error !== undefined) {
    return context.error;
  }

  if (context.err !== undefined) {
    return context.err;
  }

  return undefined;
}

function serializeLogContext(context?: LogContext) {
  const serializedContext: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(context ?? {})) {
    if (key === 'sentry') {
      continue;
    }

    serializedContext[key] = serializeLogValue(value);
  }

  return serializedContext;
}

function serializeLogValue(value: unknown) {
  if (value instanceof Error) {
    return normalizeError(value);
  }

  return value;
}

export function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      errorMessage: error.message,
      errorName: error.name,
      errorStack: error.stack,
    } satisfies LogContext;
  }

  return {
    errorMessage: String(error),
    errorName: 'UnknownError',
  } satisfies LogContext;
}
